import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { blogAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye, EyeOff, Calendar, X, Image as ImageIcon, Upload, Video, GripVertical, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

// Resize an image File to maxWidth, returning a new File (JPEG, 85% quality)
const resizeImageFile = (file, maxWidth = 1400) =>
  new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => {
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' })),
          'image/jpeg',
          0.85
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const BlogAdminPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [deletePostId, setDeletePostId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const fileInputRef = useRef(null);

  const emptyForm = { title: '', slug: '', excerpt: '', content: '', images: [], video_url: '', is_published: true };
  const [form, setForm] = useState(emptyForm);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await blogAPI.getAll(false);
      setPosts(response.data);
    } catch {
      toast.error('Eroare la sincronizarea blogului');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleOpenModal = (post = null) => {
    if (post) {
      setEditingPost(post);
      setForm({
        title: post.title, slug: post.slug, excerpt: post.excerpt,
        content: post.content, images: post.images || [],
        video_url: post.video_url || '', is_published: post.is_published,
      });
    } else {
      setEditingPost(null);
      setForm(emptyForm);
    }
    setShowModal(true);
  };

  const generateSlug = (title) =>
    title.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setForm(f => ({ ...f, title, slug: editingPost ? f.slug : generateSlug(title) }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (fileInputRef.current) fileInputRef.current.value = '';

    setUploadingImage(true);
    const newUrls = [];
    for (const file of files) {
      try {
        const resized = await resizeImageFile(file);
        const res = await blogAPI.uploadImage(resized);
        newUrls.push(res.data.url);
      } catch {
        toast.error(`Eroare la imaginea ${file.name}`);
      }
    }
    if (newUrls.length) {
      setForm(prev => ({ ...prev, images: [...prev.images, ...newUrls] }));
      toast.success(`${newUrls.length} ${newUrls.length === 1 ? 'imagine încărcată' : 'imagini încărcate'}`);
    }
    setUploadingImage(false);
  };

  const moveImage = (from, to) => {
    if (to < 0 || to >= form.images.length) return;
    const imgs = [...form.images];
    const [item] = imgs.splice(from, 1);
    imgs.splice(to, 0, item);
    setForm(f => ({ ...f, images: imgs }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, video_url: form.video_url.trim() || null };
    try {
      if (editingPost) {
        await blogAPI.update(editingPost.id, payload);
        toast.success('Articolul a fost actualizat');
      } else {
        await blogAPI.create(payload);
        toast.success('Articol publicat cu succes!');
      }
      setShowModal(false);
      fetchPosts();
    } catch {
      toast.error('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await blogAPI.delete(deletePostId);
      toast.success('Articol șters definitiv');
      setDeletePostId(null);
      fetchPosts();
    } catch { toast.error('Eroare sistem'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight">CMS Blog</h1>
          <p className="text-white/40 text-sm mt-1">Gestionează noutățile clubului</p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn-primary px-4 py-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest">
          <Plus size={16} /> Scrie Articol
        </button>
      </div>

      {loading ? (
        <div className="bg-[#0A0A0A] border border-white/5 p-20 text-center text-[#6db025] animate-pulse font-heading text-xs tracking-widest uppercase rounded">Se preia conținutul...</div>
      ) : posts.length === 0 ? (
        <div className="bg-[#0A0A0A] border border-white/5 p-20 text-center text-white/30 text-xs font-bold uppercase tracking-widest rounded">Fără conținut publicat</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {posts.map((post) => (
            <div key={post.id} className={`relative bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors rounded overflow-hidden ${!post.is_published ? 'opacity-70' : ''}`}>
              <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${post.is_published ? 'bg-[#6db025]' : 'bg-yellow-500'}`} />
              <div className="p-4 pl-5 flex items-start gap-4">
                <div className="w-24 h-16 bg-[#050505] overflow-hidden flex-shrink-0 border border-white/10 rounded flex items-center justify-center relative">
                  {post.images?.[0] ? <img src={post.images[0]} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-white/10" />}
                  {post.video_url && (
                    <div className="absolute bottom-0.5 right-0.5 w-4 h-4 bg-red-500/80 rounded-full flex items-center justify-center">
                      <Video size={9} className="text-white" />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-white font-bold uppercase tracking-tight truncate text-base">{post.title}</h3>
                    {!post.is_published ? (
                      <span className="text-[10px] uppercase px-1.5 py-0.5 bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 font-black tracking-widest rounded">Draft</span>
                    ) : (
                      <span className="text-[10px] uppercase px-1.5 py-0.5 bg-green-500/10 text-green-500 border border-green-500/20 font-black tracking-widest rounded">Public</span>
                    )}
                    {post.images?.length > 1 && (
                      <span className="text-[10px] uppercase px-1.5 py-0.5 bg-white/5 text-white/40 font-black tracking-widest rounded">{post.images.length} foto</span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-white/40 text-[11px] uppercase font-bold tracking-widest mt-1">
                    <span className="flex items-center gap-1.5"><Calendar size={13} /> {format(new Date(post.created_at), 'dd MMM yyyy', { locale: ro })}</span>
                    <span className="opacity-60 truncate">/blog/{post.slug}</span>
                  </div>
                  <div className="flex items-center justify-end gap-1 mt-3 pt-2 border-t border-white/[0.04]">
                    <Link to={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-white/40 hover:text-[#6db025] hover:bg-[#6db025]/10 transition-colors rounded" title="Vizualizează pe blog"><ExternalLink size={18} /></Link>
                    <button onClick={() => handleOpenModal(post)} className="p-2 text-white/40 hover:text-white hover:bg-white/5 transition-colors rounded" title="Editează"><Edit size={18} /></button>
                    <button onClick={() => setDeletePostId(post.id)} className="p-2 text-red-500/50 hover:text-red-500 hover:bg-red-500/10 transition-colors rounded" title="Șterge"><Trash2 size={18} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto p-0 shadow-2xl">
          <DialogHeader className="p-6 border-b border-white/5 sticky top-0 bg-[#121212] z-10">
            <DialogTitle className="font-heading text-xl text-white uppercase">{editingPost ? 'Modificare Postare' : 'Editor Conținut Nou'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Title + Slug */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1.5">
                <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Titlul Articolului</label>
                <input type="text" value={form.title} onChange={handleTitleChange} required className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white outline-none" placeholder="Ex: Start cursuri 2026" />
              </div>
              <div className="space-y-1.5">
                <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Link Direct (Slug)</label>
                <input type="text" value={form.slug} onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))} required className="w-full bg-[#050505] border border-white/10 p-4 text-white/50 outline-none" readOnly />
              </div>
            </div>

            {/* Excerpt */}
            <div className="space-y-1.5">
              <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Rezumat (Apare în lista publică)</label>
              <textarea value={form.excerpt} onChange={(e) => setForm(f => ({ ...f, excerpt: e.target.value }))} required rows={2} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white resize-none outline-none" placeholder="Scurtă descriere..." />
            </div>

            {/* Content */}
            <div className="space-y-1.5">
              <label className="text-white/30 text-[10px] font-black uppercase tracking-widest">Corpul Textului</label>
              <textarea value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))} required rows={12} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white resize-none outline-none font-mono text-sm leading-relaxed" placeholder="Scrie aici conținutul..." />
            </div>

            {/* Images */}
            <div className="space-y-3 bg-white/[0.02] p-6 border border-white/5 rounded-sm">
              <div className="flex items-center justify-between">
                <label className="text-white/40 text-[10px] font-black uppercase tracking-widest">Galerie Imagini</label>
                <span className="text-white/20 text-[9px] uppercase font-black tracking-widest">Redimensionate automat la 1400px · Stocat pe Cloudinary</span>
              </div>

              <input type="file" accept="image/*" multiple ref={fileInputRef} onChange={handleImageUpload} className="hidden" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                className="bg-white/5 border border-white/10 hover:border-[#6db025]/50 hover:bg-[#6db025]/5 hover:text-[#6db025] text-white/60 px-6 py-4 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Upload size={16} /> {uploadingImage ? 'Se încarcă...' : 'Selectează imagini (multiple)'}
              </button>

              {form.images.length > 0 && (
                <div className="pt-4 mt-2 border-t border-white/5 space-y-2">
                  <p className="text-white/20 text-[9px] uppercase font-black tracking-widest">Prima imagine = copertă · Săgeți pentru reordonare</p>
                  <div className="flex flex-wrap gap-3">
                    {form.images.map((img, i) => (
                      <div key={i} className="relative group w-28 h-20 border border-white/10 bg-black overflow-hidden shadow-xl rounded-sm">
                        <img src={img} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                        {i === 0 && (
                          <div className="absolute top-1 left-1 bg-[#6db025] text-black text-[8px] font-black uppercase px-1.5 py-0.5 rounded-sm">
                            Copertă
                          </div>
                        )}
                        <div className="absolute inset-0 flex flex-col justify-between p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="flex justify-end">
                            <button type="button" onClick={() => setForm(f => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))} className="w-5 h-5 bg-red-500/90 hover:bg-red-500 flex items-center justify-center rounded-sm">
                              <X size={10} className="text-white" />
                            </button>
                          </div>
                          <div className="flex justify-between">
                            <button type="button" onClick={() => moveImage(i, i - 1)} disabled={i === 0} className="w-5 h-5 bg-black/60 hover:bg-black flex items-center justify-center rounded-sm disabled:opacity-20 text-white text-xs">←</button>
                            <button type="button" onClick={() => moveImage(i, i + 1)} disabled={i === form.images.length - 1} className="w-5 h-5 bg-black/60 hover:bg-black flex items-center justify-center rounded-sm disabled:opacity-20 text-white text-xs">→</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Video URL */}
            <div className="space-y-1.5">
              <label className="text-white/30 text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                <Video size={12} /> URL Video - YouTube / Vimeo <span className="text-white/20 normal-case font-normal">(opțional)</span>
              </label>
              <input
                type="url"
                value={form.video_url}
                onChange={(e) => setForm(f => ({ ...f, video_url: e.target.value }))}
                placeholder="https://www.youtube.com/watch?v=... sau https://vimeo.com/..."
                className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#6db025] p-4 text-white outline-none placeholder:text-white/20"
              />
              {form.video_url && (
                <p className="text-white/30 text-[9px] uppercase font-black tracking-widest">
                  Video va apărea în articol după galeria foto.
                </p>
              )}
            </div>

            {/* Publish + Save */}
            <div className="flex flex-col sm:flex-row items-center gap-4 pt-4 border-t border-white/5">
              <button type="button" onClick={() => setForm(f => ({ ...f, is_published: !f.is_published }))} className={`flex items-center gap-2 px-6 py-4 font-bold uppercase text-[10px] tracking-widest transition-colors flex-1 justify-center sm:flex-none ${form.is_published ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-white/5 text-white/40 border border-white/10'}`}>
                {form.is_published ? <><Eye size={16} /> Articolul va fi Public</> : <><EyeOff size={16} /> Salvează ca Draft Ascuns</>}
              </button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 py-4 uppercase font-black tracking-widest text-sm disabled:opacity-20 w-full">
                {saving ? 'Se salvează...' : 'Salvează Postarea'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletePostId} onOpenChange={() => setDeletePostId(null)}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white uppercase font-heading text-red-500">Eliminare Articol</AlertDialogTitle>
            <AlertDialogDescription className="text-white/40">Această acțiune va șterge postarea de pe blogul public.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-0 text-white">Anulează</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 text-white border-0">Șterge Definitiv</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BlogAdminPage;
